import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SUPPORT_EMAIL, TELEGRAM_SUPPORT_URL } from "@/config/site";

const FAQ = [
  {
    q: "When will I receive my code?",
    a: "Digital delivery by email. Codes are verified and sent manually after payment — usually within a few hours, and always within 24 hours. You can follow every stage on your order confirmation page.",
  },
  {
    q: "What's the difference between region and currency?",
    a: "Your account region determines which gift-card codes you can redeem. Currency only changes how prices are displayed and paid. Both are always visible in the header, and you can change them any time.",
  },
  {
    q: "Do I need an account to buy?",
    a: "No. Guest checkout is the default. Creating an account just keeps your orders and codes in one place.",
  },
  {
    q: "Which payment methods can I use?",
    a: "Payments are processed securely by Razorpay. For India you can pay by UPI or credit/debit card; other regions can pay by credit/debit card.",
  },
  {
    q: "Can I get a refund?",
    a: "See our Refund Policy page for details.",
    link: { to: "/refund", label: "View Refund Policy" },
  },
  {
    q: "How do I contact support?",
    a: `Use the Telegram support button on any page, or email us at ${SUPPORT_EMAIL}. We typically respond as soon as possible.`,
  },
];

export default function Support() {
  return (
    <div className="vault-container pt-16 sm:pt-20" data-testid="support-page">
      <Reveal>
        <p className="overline-label">Support</p>
        <h1 className="mt-3 font-display text-4xl font-semibold tracking-tighter sm:text-5xl">
          Help / FAQ
        </h1>
        <p className="mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground">
          Quick answers about delivery, regions, payments, and your account.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <Accordion type="single" collapsible className="mt-12 max-w-3xl" data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-white/[0.08]">
              <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-display text-base tracking-tight">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                {f.a}
                {f.link && (
                  <Link to={f.link.to} className="mt-2 inline-block text-primary underline-offset-4 hover:underline">
                    {f.link.label}
                  </Link>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="mt-12 max-w-3xl rounded-2xl border border-white/[0.08] bg-card p-7">
          <h2 className="font-display text-lg font-semibold tracking-tight">Still stuck?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We typically respond as soon as possible.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {TELEGRAM_SUPPORT_URL ? (
              <a
                data-testid="support-telegram-link"
                href={TELEGRAM_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-[#0088CC] px-5 py-2.5 text-sm font-medium text-white transition-transform duration-200 hover:scale-[1.03]"
              >
                Chat on Telegram
              </a>
            ) : (
              <span
                data-testid="support-telegram-link"
                className="cursor-not-allowed rounded-full bg-[#0088CC]/40 px-5 py-2.5 text-sm font-medium text-white/70"
              >
                Telegram support — coming soon
              </span>
            )}
            <Link
              to="/contact"
              data-testid="support-contact-link"
              className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
            >
              Contact page
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
